import { PieChart } from '@mui/x-charts-pro';
import { legendClasses } from '@mui/x-charts-pro/ChartsLegend';
import { useDrawingArea } from '@mui/x-charts-pro/hooks';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ChartData } from '../../../store/Charts';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { TextSpan } from '../../Atoms/Text';
import { getSelectedTab, updateState, FilterableField } from '../../../store/GeneralStore';

interface DonutChartProps {
  data: ChartData[];
  loading: boolean;
  title: string;
}

interface getLinkType {
  text: string;
  url: string;
  index: number;
  key: string;
  filterField: string;
}

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 14
}));

function PieCenterLabel({ label, children }: { label: string; children: React.ReactNode }) {
  const { height, left, top, width } = useDrawingArea();
  const xCenter = left + width / 2;
  const yCenter = top + height / 2;
  const yOffset = 11; // Offset for the label position
  return (
    <>
      <StyledText
        x={xCenter}
        y={yCenter - yOffset}>
        {children}
      </StyledText>
      <StyledText
        x={xCenter}
        y={yCenter + yOffset}>
        {label}
      </StyledText>
    </>
  );
}

export const DonutChart = ({ data, loading, title }: DonutChartProps) => {
  const isCurrencyChart = title === 'Open BnC Value';
  const processedData = data.map((item) => {
    return {
      ...item,
      value: typeof item.value === 'string' ? parseFloat(item.value.replace(/[^0-9.-]/g, '')) : item.value
    };
  });
  const totalValue = processedData.reduce((acc, item) => acc + (typeof item.value === 'number' ? item.value : 0), 0);
  const navigate = useNavigate();
  const labelByTitleMap: Record<string, string> = {
    'Open BnC Value': 'MRR',
    'Disconnected Not Decommissioned': 'Assets',
    'Overdue Follow-Ups': 'Follow-Ups',
    'Overdue Tickets': 'Tickets',
    'Validation Needed': 'Assets',
    'Missing Link': 'Assets',
    'Original Still Commissioning': 'Assets',
    'Original and Renewal Overlap': 'Assets'
  };

  const getLink = (title: string) => {
    const links: Record<string, getLinkType> = {
      'Open BnC Value': {
        text: 'View Assets',
        url: '/asset',
        index: 2,
        key: 'assets',
        filterField: 'commissionsExperienceAnalystEmail'
      },
      'Disconnected Not Decommissioned': {
        text: 'View Assets',
        url: '/asset',
        index: 8,
        key: 'assets',
        filterField: 'commissionsExperienceAnalystEmail'
      },
      'Overdue Follow-Ups': {
        text: 'View Follow-Ups',
        url: '/ticket-follow-ups',
        index: 2,
        key: 'tfus',
        filterField: 'owner.email'
      },
      'Overdue Tickets': {
        text: 'View Tickets',
        url: '/ticket',
        index: 3,
        key: 'tickets',
        filterField: 'assignedTo.email'
      },
      'Validation Needed': {
        text: 'View Assets',
        url: '/asset',
        index: 9,
        key: 'assets',
        filterField: 'commissionsExperienceAnalystEmail'
      },
      'Missing Link': {
        text: 'View Assets',
        url: '/asset',
        index: 6,
        key: 'assets',
        filterField: 'commissionsExperienceAnalystEmail'
      },
      'Original Still Commissioning': {
        text: 'View Assets',
        url: '/asset',
        index: 10,
        key: 'assets',
        filterField: 'commissionsExperienceAnalystEmail'
      },
      'Original and Renewal Overlap': {
        text: 'View Assets',
        url: '/asset',
        index: 11,
        key: 'assets',
        filterField: 'commissionsExperienceAnalystEmail'
      }
    };
    return links[title] || null;
  };

  const link = getLink(title);

  function handleClick(data?: ChartData) {
    updateState({
      selectedTab: {
        ...getSelectedTab(),
        [link.key]: link.index
      }
    });
    let url = link.url;
    if (data) {
      let filters: FilterableField[] = [];
      const params = new URLSearchParams();
      if (data.email) {
        filters = [{ field: link.filterField, operator: 'equals', value: data.email }];
      } else {
        filters = [{ field: link.filterField, operator: 'isEmpty', value: '' }];
      }
      params.set('filters', JSON.stringify(filters));
      url += `?${params.toString()}`;
    }
    navigate(url);
  }

  const formatChartValue = (value: number) => {
    const roundedValue = Math.round(value);
    return isCurrencyChart ? `$${roundedValue.toLocaleString('en-US')}` : roundedValue.toLocaleString('en-US');
  };

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-grey-3">
      <div className="w-full min-w-[290px] max-w-[400px]">
        <div className="text-center mt-1">
          <TextSpan
            className="text-center mt-3"
            color="grey9"
            size="sm14">
            {title}
          </TextSpan>
        </div>
        {loading && (
          <div className="flex justify-center items-center h-[180px]">
            <ComponentSpinner />
          </div>
        )}
        {!loading && (
          <PieChart
            series={[
              {
                data: processedData,
                innerRadius: 60,
                outerRadius: 75,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                valueFormatter: (value: any) => formatChartValue(value.value)
              }
            ]}
            onItemClick={(event, d) => {
              handleClick(processedData[d.dataIndex]);
            }}
            slotProps={{
              legend: {
                sx: {
                  fontSize: 11,
                  gap: '5px',
                  [`.${legendClasses.mark}`]: {
                    height: 11,
                    width: 11
                  },
                  ['.MuiChartsLegend-series']: {
                    gap: '5px'
                  }
                },
                position: { vertical: 'middle', horizontal: 'start' }
              }
            }}
            height={170}>
            <PieCenterLabel label={labelByTitleMap[title]}>{formatChartValue(totalValue)}</PieCenterLabel>
          </PieChart>
        )}
        {link && (
          <button
            className="text-xs text-indigo mb-2 ml-3"
            onClick={() => handleClick()}>
            {link.text}
          </button>
        )}
      </div>
    </div>
  );
};
